import { useQuery } from '@tanstack/react-query';
import AxiosClient from '../../api/client';

// =====================================================
// TYPES & INTERFACES
// =====================================================

export interface DashboardOverviewStats {
  total_properties: number;
  active_properties: number;
  inactive_properties: number;
  total_users: number;
  total_assessments: number;
  assessments_this_month: number;
  completed_assessments: number;
  completion_rate: number;
  average_score: number;
  overdue_assessments: number;
  upcoming_due_this_week: number;
}

export interface MonthlyTrend {
  month: string;
  month_key: string;
  assessment_count: number;
  average_score: number | null;
}

export interface DepartmentPerformance {
  id: number;
  name: string;
  score: number;
  total_standards_checked: number;
  compliant: number;
  non_compliant: number;
}

export interface PropertyTypeBreakdown {
  id: number;
  name: string;
  icon: string | null;
  color: string | null;
  property_count: number;
  assessment_count: number;
  average_score: number;
}

export interface PropertyPerformance {
  id: number;
  name: string;
  type: string;
  stars: number;
  score: number;
  assessment_count: number;
  is_active: boolean;
}

export interface RecentAssessment {
  id: string;
  property_id: number;
  property_name: string;
  subject: string;
  inspector_name: string;
  created_at: string;
  due_date: string;
  score: number | null;
  is_completed: boolean;
  is_overdue: boolean;
}

export interface InspectorPerformance {
  id: number;
  name: string;
  email: string;
  assigned_count: number;
  completed_count: number;
  completion_rate: number;
  average_score: number;
}

export interface DashboardAnalytics {
  overview: DashboardOverviewStats;
  monthly_trends: MonthlyTrend[];
  department_performance: DepartmentPerformance[];
  property_type_breakdown: PropertyTypeBreakdown[];
  top_performers: PropertyPerformance[];
  bottom_performers: PropertyPerformance[];
  recent_assessments: RecentAssessment[];
  inspector_performance: InspectorPerformance[];
}

export interface DashboardAnalyticsResponse {
  status: boolean;
  data: DashboardAnalytics;
  error: string | null;
  detail: string;
}

// =====================================================
// API HOOK
// =====================================================

/**
 * Fetch comprehensive dashboard analytics
 * Includes: overview stats, trends, department/property performance,
 * recent activity, inspector performance
 *
 * @returns TanStack Query result with dashboard analytics data
 */
export function useDashboardAnalytics() {
  return useQuery({
    queryKey: ['dashboard', 'analytics'],
    queryFn: async (): Promise<DashboardAnalytics> => {
      const client = new AxiosClient<never, DashboardAnalyticsResponse>({
        endpoint: '/dashboard/analytics',
        isToken: true,
      });

      const response = await client.instance.get<DashboardAnalyticsResponse>(
        client.endpoint
      );

      if (!response.data.status) {
        throw new Error(
          response.data.detail || 'Failed to fetch dashboard analytics'
        );
      }

      return response.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60 * 5, // Auto-refresh every 5 minutes
  });
}
